import React, { useState, useEffect } from 'react';
import { X, Check, Building2, Truck, AlertCircle, Loader2, MapPin, Smartphone, CreditCard } from 'lucide-react';
import confetti from 'canvas-confetti';
import { CartItem, DistributorConfig, OrderRecord, PaymentMethod } from '../types';
import { GIFFARINE_OFFICIAL_LOGO } from '../assets/logo';
import { PinVerificationModal } from './PinVerificationModal';
import { ThaiQrPaymentBadge } from './payment/ThaiQrPaymentBadge';
import { db, secureSetDoc, doc } from '../firebase';
import { searchThaiAddress } from '../lib/thaiAddressesDb';
import { ThaiAddress } from '../data/thaiAddresses';
import { 
  subscribePaymentSettings, 
  PaymentMethodsSettings, 
  getCachedPaymentSettings 
} from '../lib/paymentSettings';
import { 
  getOrCreateDeviceId, 
  checkCheckoutPinRequirement, 
  recordDeviceOrderSuccess, 
  PinRequirementCheck 
} from '../lib/deviceSecurity';

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  cart: CartItem[];
  distributor: DistributorConfig;
  onOrderSuccess: (order: OrderRecord) => void;
  currentUser?: any;
  onOpenAuth: () => void;
}

export const CheckoutModal: React.FC<CheckoutModalProps> = ({
  isOpen,
  onClose,
  cart,
  distributor,
  onOrderSuccess,
  currentUser,
  onOpenAuth,
}) => {
  if (!isOpen) return null;

  // Customer shipping information states
  const [customerName, setCustomerName] = useState('');
  const [phone, setPhone] = useState('');
  
  // Split address into 3 fields
  const [addressPart1, setAddressPart1] = useState(''); // บ้านเลขที่ หมู่บ้าน ถนน
  const [addressPart2, setAddressPart2] = useState(''); // ต/อ/จ
  const [addressPart3, setAddressPart3] = useState(''); // รหัสไปรษณีย์
  
  // Address Autocomplete suggestions states
  const [addressSuggestions, setAddressSuggestions] = useState<ThaiAddress[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeFocusField, setActiveFocusField] = useState<'part2' | 'part3' | null>(null);

  const handlePart2Change = async (val: string) => {
    setAddressPart2(val);
    setActiveFocusField('part2');
    if (val.trim().length >= 2) {
      const res = await searchThaiAddress(val);
      setAddressSuggestions(res);
      setShowSuggestions(true);
    } else {
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handlePart3Change = async (val: string) => {
    const cleaned = val.replace(/\D/g, '');
    setAddressPart3(cleaned);
    setActiveFocusField('part3');
    if (cleaned.length >= 2) {
      const res = await searchThaiAddress(cleaned);
      setAddressSuggestions(res);
      setShowSuggestions(true);
    } else {
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectAddress = (item: ThaiAddress) => {
    setAddressPart2(`ต.${item.s} อ.${item.d} จ.${item.p}`);
    setAddressPart3(item.z);
    setShowSuggestions(false);
    setAddressSuggestions([]);
  };
  
  const [notes, setNotes] = useState('');

  // Payment channel: Dynamic from Admin Dashboard settings
  const [paymentSettings, setPaymentSettings] = useState<PaymentMethodsSettings>(() => getCachedPaymentSettings());
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('COD');
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Transaction PIN Gate State
  const [isPinModalOpen, setIsPinModalOpen] = useState(false);
  const [pinCheckData, setPinCheckData] = useState<PinRequirementCheck | null>(null);

  // Parse unified address into 3 parts
  const parseAddressParts = (savedAddress: string) => {
    const fullAddr = savedAddress.trim();
    // Extract 5-digit postal code at the end
    const zipMatch = fullAddr.match(/\b\d{5}$/);
    let zip = '';
    let remaining = fullAddr;
    if (zipMatch) {
      zip = zipMatch[0];
      remaining = fullAddr.slice(0, zipMatch.index).trim();
      if (remaining.endsWith(',')) remaining = remaining.slice(0, -1).trim();
    }

    // Split remaining address to extract Tumbon/Amphoe/Province (ต/อ/จ)
    // Look for common keywords: ต. / ตำบล / แขวง / อ. / อำเภอ / เขต / จ. / จังหวัด / กรุงเทพ
    const districtKeywords = ['ต.', 'ตำบล', 'แขวง', 'อ.', 'อำเภอ', 'เขต', 'จ.', 'จังหวัด', 'กรุงเทพ'];
    let part1 = remaining;
    let part2 = '';

    // Find the index of the first keyword
    let earliestKeywordIndex = -1;
    for (const kw of districtKeywords) {
      const idx = remaining.indexOf(kw);
      if (idx !== -1 && (earliestKeywordIndex === -1 || idx < earliestKeywordIndex)) {
        earliestKeywordIndex = idx;
      }
    }

    if (earliestKeywordIndex !== -1) {
      part1 = remaining.substring(0, earliestKeywordIndex).trim();
      part2 = remaining.substring(earliestKeywordIndex).trim();
      // Remove trailing commas
      if (part1.endsWith(',')) part1 = part1.slice(0, -1).trim();
    } else {
      // Fallback split
      const parts = remaining.split(/[\s,]+/);
      if (parts.length >= 2) {
        const mid = Math.floor(parts.length / 2);
        part1 = parts.slice(0, mid).join(' ');
        part2 = parts.slice(mid).join(' ');
      }
    }

    return { part1, part2, zip };
  };

  // Compile address from the 3 parts
  const getCompiledAddress = () => {
    const p1 = addressPart1.trim();
    const p2 = addressPart2.trim();
    const p3 = addressPart3.trim();
    return `${p1} ${p2} ${p3}`.trim();
  };

  // Load prefilled name and address from logged-in user profile
  useEffect(() => {
    if (currentUser) {
      if (!customerName) setCustomerName(currentUser.fullName || '');
      if (!phone) setPhone(currentUser.phoneNumber || '');
      
      const savedAddress = currentUser.shippingAddress || '';
      if (savedAddress && !addressPart1 && !addressPart2 && !addressPart3) {
        const parsed = parseAddressParts(savedAddress);
        setAddressPart1(parsed.part1);
        setAddressPart2(parsed.part2);
        setAddressPart3(parsed.zip);
      }
    }
  }, [currentUser]);

  // Sync Dynamic Payment Methods from Admin Settings in Real-Time
  useEffect(() => {
    const unsub = subscribePaymentSettings((settings) => {
      setPaymentSettings(settings);
      setPaymentMethod((current) => {
        if (current === 'COD' && settings.cod) return 'COD';
        if (current === 'PROMPTPAY' && settings.qr) return 'PROMPTPAY';
        if (current === 'CREDIT_CARD' && settings.card) return 'CREDIT_CARD';

        // Auto fallback to first available active payment method
        if (settings.cod) return 'COD';
        if (settings.qr) return 'PROMPTPAY';
        if (settings.card) return 'CREDIT_CARD';
        return 'COD';
      });
    });

    return () => {
      unsub();
    };
  }, []);

  // Calculations
  const subtotal = cart.reduce(
    (sum, item) => sum + item.product.memberPrice * item.quantity,
    0
  );
  const isFreeShipping = subtotal >= distributor.freeShippingThreshold;
  const shippingFee = isFreeShipping ? 0 : distributor.shippingFee;
  const totalAmount = subtotal + shippingFee;

  // 1. Initial Submit handler (Validates input and checks PIN)
  const handleSubmitOrder = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    // Validation
    const finalName = customerName.trim();
    const finalPhone = phone.trim();

    if (!finalName) {
      setErrorMessage('กรุณาระบุชื่อ-นามสกุล ผู้รับสินค้า');
      return;
    }
    if (!finalPhone || finalPhone.length < 9) {
      setErrorMessage('กรุณาระบุเบอร์โทรศัพท์ติดต่อให้ถูกต้อง');
      return;
    }
    if (!addressPart1.trim()) {
      setErrorMessage('กรุณากรอก บ้านเลขที่, หมู่บ้าน/อาคาร, ซอย, ถนน ในช่องที่ 1');
      return;
    }
    if (!addressPart2.trim()) {
      setErrorMessage('กรุณากรอก ตำบล/อำเภอ/จังหวัด ในช่องที่ 2');
      return;
    }
    if (!addressPart3.trim() || addressPart3.trim().length !== 5) {
      setErrorMessage('กรุณากรอก รหัสไปรษณีย์ 5 หลักให้ถูกต้อง ในช่องที่ 3');
      return;
    }

    // Security check: Must be authenticated to order
    if (!currentUser) {
      setErrorMessage('กรุณาเข้าสู่ระบบหรือสมัครสมาชิกผู้บริโภคกิฟฟารีน เพื่อยืนยันตัวตนความปลอดภัยของบัญชีและคะแนนสะสม PV');
      onOpenAuth();
      return;
    }

    // Check PIN requirement based on device history & order amount threshold (> 2500 THB)
    const userKey = currentUser?.customerId || currentUser?.memberId || currentUser?.id || currentUser?.email || 'GUEST';
    const pinCheck = checkCheckoutPinRequirement({
      userKey,
      totalAmount
    });

    if (pinCheck.requiresPin) {
      setPinCheckData(pinCheck);
      setIsPinModalOpen(true);
    } else {
      // Auto-bypassed on trusted device (order 2+ and <= 2500 THB)
      executeOrderSubmission();
    }
  };

  // 2. Verified submission logic (Triggered after 6-digit PIN successfully verified)
  const executeOrderSubmission = async () => {
    const finalName = customerName.trim();
    const finalPhone = phone.trim();
    const finalAddress = getCompiledAddress();

    setSubmittingOrder(true);

    try {
      // Order items payload
      const orderItems = cart.map(item => ({
        productCode: item.product.code,
        productName: item.product.name,
        quantity: item.quantity,
        pricePerUnit: item.product.memberPrice,
        pv: item.product.pv,
      }));

      // Send to /api/order
      const orderRes = await fetch('/api/order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerName: finalName,
          phone: finalPhone,
          address: finalAddress,
          items: orderItems,
          paymentMethod: paymentMethod === 'PROMPTPAY' ? 'PROMPTPAY' : paymentMethod === 'CREDIT_CARD' ? 'CREDIT_CARD' : 'COD',
          slipUrl: '',
          notes,
          customerId: currentUser?.customerId ? String(currentUser.customerId).trim() : '',
          userId: currentUser?.id || '',
          customerEmail: currentUser?.email || '',
          email: currentUser?.email || '',
        }),
      });

      const orderData = await orderRes.json();

      if (!orderRes.ok || !orderData.success) {
        throw new Error(orderData.error || 'เกิดข้อผิดพลาดในการบันทึกคำสั่งซื้อ');
      }

      // Save order to Firestore durable cloud database in parallel
      try {
        if (orderData.order) {
          await secureSetDoc(
            doc(db, 'orders', orderData.order.orderId), 
            {
              ...orderData.order,
              userId: currentUser?.id || '',
              userEmail: currentUser?.email || '',
              createdAt: new Date().toISOString()
            }, 
            `orders/${orderData.order.orderId}`
          );

          if (currentUser?.id) {
            await secureSetDoc(
              doc(db, 'users', currentUser.id, 'private', 'profile'), 
              {
                fullName: finalName,
                phoneNumber: finalPhone,
                shippingAddress: finalAddress,
                updatedAt: new Date().toISOString()
              }, 
              `users/${currentUser.id}/private/profile`
            );
          }
        }
      } catch (fsErr) {
        console.warn('Firestore parallel order persistence note:', fsErr);
      }

      // Trigger Confetti Celebration
      try {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#E60067', '#10B981', '#F59E0B'],
        });
      } catch (err) {
        // Safe fallback if confetti fails in iframe
      }

      // Trigger Success Callback (will open SalesSlipModal)
      try {
        const userKey = currentUser?.customerId || currentUser?.memberId || currentUser?.id || currentUser?.email || 'GUEST';
        const deviceId = getOrCreateDeviceId();
        recordDeviceOrderSuccess({
          userKey,
          orderId: orderData.order.orderId,
          totalAmount,
          deviceId
        });
        window.dispatchEvent(new CustomEvent('order-created', { detail: orderData.order }));
      } catch (e) {}
      // If QR Payment (PromptPay) selected: redirect to QR payment processing page
      if (paymentMethod === 'PROMPTPAY') {
        const effectiveMemberId = currentUser?.customerId || orderData.order.customerId || '98939522';
        const slug = `${effectiveMemberId}-${orderData.order.orderId}`;
        const targetUrl = `/checkout/${slug}/qr-payment-processing`;

        // Seamless SPA navigation
        window.history.pushState({}, '', targetUrl);
        window.dispatchEvent(new Event('popstate'));
        window.scrollTo({ top: 0, behavior: 'smooth' });
        onClose();
        return;
      }

      onOrderSuccess(orderData.order);
      onClose();
    } catch (err: any) {
      console.error('Submit order error:', err);
      setErrorMessage(err?.message || 'ไม่สามารถบันทึกคำสั่งซื้อได้ กรุณาลองใหม่อีกครั้ง');
    } finally {
      setSubmittingOrder(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-xs p-3 sm:p-4 overflow-y-auto">
      <div className="relative w-full max-w-lg my-auto bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden border border-pink-100">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3.5 sm:py-4 border-b border-blue-900 bg-gradient-to-r from-[#0E2C58] to-[#1E3A8A] text-white">
          <div className="flex items-center gap-2.5 sm:gap-3">
            <div className="px-2 sm:px-2.5 py-1 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center shadow-xs">
              <img
                src={GIFFARINE_OFFICIAL_LOGO}
                alt="GIFFARINE"
                className="h-4.5 sm:h-5.5 w-auto object-contain brightness-0 invert"
              />
            </div>
            <div>
              <h2 className="text-sm sm:text-base font-bold text-white">ยืนยันคำสั่งซื้อ & จัดส่ง</h2>
              <p className="text-[11px] sm:text-xs text-blue-200">กรอกข้อมูลผู้รับเพื่อจัดส่งถึงบ้านคุณ</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-blue-200 hover:text-white rounded-full hover:bg-white/10 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmitOrder} className="p-4 sm:p-6 max-h-[82vh] overflow-y-auto space-y-4 sm:space-y-5">
          {errorMessage && (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-2xl flex items-start gap-2.5 text-xs text-rose-700">
              <AlertCircle className="w-4 h-4 shrink-0 text-rose-600 mt-0.5" />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Section 1: Standard Customer Details & Shipping Address */}
          <div className="space-y-3.5">
            <div className="flex items-center justify-between pb-1 border-b border-slate-100">
              <label className="text-xs font-bold text-slate-800 flex items-center gap-2">
                <Truck className="w-4 h-4 text-[#2A59FE]" />
                ข้อมูลผู้รับ & ที่อยู่จัดส่งสินค้า
              </label>
            </div>

            {/* Standard Form Inputs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <div>
                <label className="block text-[11px] font-bold text-slate-700 mb-1">
                  ชื่อ-นามสกุล ผู้รับ <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="เช่น คุณสมชาย ใจดี"
                  className="w-full px-3.5 py-2.5 bg-slate-50/60 border border-slate-200 rounded-xl text-xs text-slate-800 focus:bg-white focus:border-[#2A59FE] focus:ring-2 focus:ring-blue-500/15 focus:outline-none transition-all"
                  required
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-700 mb-1">
                  เบอร์โทรศัพท์ติดต่อ <span className="text-rose-500">*</span>
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="เช่น 081-234-5678"
                  className="w-full px-3.5 py-2.5 bg-slate-50/60 border border-slate-200 rounded-xl text-xs text-slate-800 focus:bg-white focus:border-[#2A59FE] focus:ring-2 focus:ring-blue-500/15 focus:outline-none font-mono transition-all"
                  required
                />
              </div>

              {/* Split Address into 3 Beautiful Input Fields */}
              <div className="sm:col-span-2 space-y-3">
                <div className="border-t border-slate-100 pt-2.5">
                  <span className="text-xs font-bold text-slate-800 block mb-2">
                    ที่อยู่จัดส่งโดยรายละเอียด <span className="text-rose-500">*</span>
                  </span>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">
                      ช่องที่ 1: บ้านเลขที่ หมู่บ้าน ถนน
                    </label>
                    <input
                      type="text"
                      value={addressPart1}
                      onChange={(e) => setAddressPart1(e.target.value)}
                      placeholder="เช่น 123/45 หมู่บ้านแสนสบาย ซอย 5 ถนนสุขุมวิท"
                      className="w-full px-3.5 py-2.5 bg-slate-50/60 border border-slate-200 rounded-xl text-xs text-slate-800 focus:bg-white focus:border-[#E60067] focus:ring-2 focus:ring-pink-500/15 focus:outline-none transition-all"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="relative">
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">
                        ช่องที่ 2: กรอก ต/อ/จ (ตำบล, อำเภอ, จังหวัด)
                      </label>
                      <input
                        type="text"
                        value={addressPart2}
                        onChange={(e) => handlePart2Change(e.target.value)}
                        onFocus={() => {
                          setActiveFocusField('part2');
                          if (addressPart2.trim().length >= 2) setShowSuggestions(true);
                        }}
                        onBlur={() => {
                          setTimeout(() => setShowSuggestions(false), 250);
                        }}
                        placeholder="เช่น ต.คลองเตย อ.คลองเตย จ.กรุงเทพมหานคร"
                        className="w-full px-3.5 py-2.5 bg-slate-50/60 border border-slate-200 rounded-xl text-xs text-slate-800 focus:bg-white focus:border-[#E60067] focus:ring-2 focus:ring-pink-500/15 focus:outline-none transition-all"
                        required
                        autoComplete="off"
                      />
                      {showSuggestions && activeFocusField === 'part2' && addressSuggestions.length > 0 && (
                        <div className="absolute left-0 right-0 z-30 mt-1 max-h-52 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-xl divide-y divide-slate-100">
                          {addressSuggestions.map((item, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => handleSelectAddress(item)}
                              className="w-full px-3.5 py-2.5 text-left text-xs text-slate-700 hover:bg-pink-50/60 flex items-start gap-2.5 transition-colors cursor-pointer"
                            >
                              <MapPin className="w-3.5 h-3.5 text-[#E60067] shrink-0 mt-0.5" />
                              <div>
                                <span className="font-bold text-slate-800">ต.{item.s} อ.{item.d}</span>
                                <p className="text-[10px] text-slate-400">จ.{item.p} {item.z}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="relative">
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">
                        ช่องที่ 3: กรอก รหัสไปรษณีย์
                      </label>
                      <input
                        type="text"
                        maxLength={5}
                        pattern="\d*"
                        value={addressPart3}
                        onChange={(e) => handlePart3Change(e.target.value)}
                        onFocus={() => {
                          setActiveFocusField('part3');
                          if (addressPart3.trim().length >= 2) setShowSuggestions(true);
                        }}
                        onBlur={() => {
                          setTimeout(() => setShowSuggestions(false), 250);
                        }}
                        placeholder="เช่น 10110"
                        className="w-full px-3.5 py-2.5 bg-slate-50/60 border border-slate-200 rounded-xl text-xs text-slate-800 focus:bg-white focus:border-[#E60067] focus:ring-2 focus:ring-pink-500/15 focus:outline-none font-mono transition-all"
                        required
                        autoComplete="off"
                      />
                      {showSuggestions && activeFocusField === 'part3' && addressSuggestions.length > 0 && (
                        <div className="absolute left-0 right-0 z-30 mt-1 max-h-52 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-xl divide-y divide-slate-100">
                          {addressSuggestions.map((item, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => handleSelectAddress(item)}
                              className="w-full px-3.5 py-2.5 text-left text-xs text-slate-700 hover:bg-pink-50/60 flex items-start gap-2.5 transition-colors cursor-pointer"
                            >
                              <MapPin className="w-3.5 h-3.5 text-[#E60067] shrink-0 mt-0.5" />
                              <div>
                                <span className="font-bold text-slate-800">{item.z}</span>
                                <p className="text-[10px] text-slate-400">ต.{item.s} อ.{item.d} จ.{item.p}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-[11px] font-medium text-slate-500 mb-1">
                  หมายเหตุเพิ่มเติม (ถ้ามี)
                </label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="เช่น ฝากไว้ที่ป้อมยาม, โทรหาก่อนส่ง"
                  className="w-full px-3.5 py-2 bg-slate-50/40 border border-slate-200 rounded-xl text-xs text-slate-700 focus:bg-white focus:border-[#E60067] focus:outline-none transition-all"
                />
              </div>
            </div>
          </div>

          {/* Section 2: Payment Channel - Dynamic from Admin Settings */}
          <div className="space-y-2.5 pt-2 border-t border-slate-100">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-slate-800 block">
                วิธีชำระเงิน
              </label>
              <span className="text-[10px] text-slate-400">
                {[paymentSettings.cod, paymentSettings.qr, paymentSettings.card].filter(Boolean).length > 1
                  ? 'เลือกช่องทางที่สะดวก'
                  : 'ช่องทางที่เปิดรับชำระ'}
              </span>
            </div>

            <div className="space-y-2.5">
              {/* 1. COD */}
              {paymentSettings.cod && (
                <div
                  id="checkout-modal-pay-cod"
                  onClick={() => setPaymentMethod('COD')}
                  className={`p-3.5 rounded-2xl border-2 transition-all cursor-pointer flex items-start gap-3 select-none ${
                    paymentMethod === 'COD'
                      ? 'border-[#2A59FE] bg-blue-50/30 shadow-2xs'
                      : 'border-slate-200 hover:border-blue-200 bg-white'
                  }`}
                >
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                    paymentMethod === 'COD'
                      ? 'bg-[#2A59FE] text-white shadow-sm shadow-blue-500/20'
                      : 'bg-slate-100 text-slate-500'
                  }`}>
                    <Truck className="w-4.5 h-4.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1">
                      <div className="text-xs font-bold text-slate-900">
                        เก็บเงินปลายทาง (COD)
                      </div>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        paymentMethod === 'COD'
                          ? 'text-[#2A59FE] bg-blue-100/80 border border-blue-200'
                          : 'text-slate-500 bg-slate-100'
                      }`}>
                        COD
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 leading-tight">
                      ชำระเงินสดหรือสแกนจ่ายกับเจ้าหน้าที่ขนส่งเมื่อได้รับสินค้า
                    </div>
                  </div>
                  <div className={`w-4.5 h-4.5 rounded-full border flex items-center justify-center shrink-0 mt-0.5 ${
                    paymentMethod === 'COD'
                      ? 'border-[#2A59FE] bg-[#2A59FE] text-white'
                      : 'border-slate-300 bg-white'
                  }`}>
                    {paymentMethod === 'COD' && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </div>
                </div>
              )}

              {/* 2. QR Payment / THAI QR PAYMENT */}
              {paymentSettings.qr && (
                <div
                  id="checkout-modal-pay-qr"
                  onClick={() => setPaymentMethod('PROMPTPAY' as any)}
                  className={`p-3 sm:p-3.5 rounded-2xl border-2 transition-all cursor-pointer flex items-start gap-2.5 sm:gap-3 select-none ${
                    (paymentMethod as string) === 'PROMPTPAY'
                      ? 'border-[#2A59FE] bg-blue-50/20 shadow-2xs'
                      : 'border-slate-200 hover:border-blue-200 bg-white'
                  }`}
                >
                  {/* Official THAI QR PAYMENT Logo Badge */}
                  <div className="shrink-0 pt-0.5">
                    <div className="h-9 w-[84px] sm:w-[90px] flex items-center justify-center rounded-xl overflow-hidden shadow-xs border border-[#00387A] bg-[#002D63]">
                      <ThaiQrPaymentBadge className="w-full h-full object-contain" />
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center justify-between gap-1">
                      <div className="text-xs font-bold text-slate-900">
                        คิวอาร์เพย์เม้นท์ (THAI QR PAYMENT)
                      </div>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 ${
                        (paymentMethod as string) === 'PROMPTPAY'
                          ? 'bg-[#002D63] text-white'
                          : 'text-slate-500 bg-slate-100'
                      }`}>
                        <span className="w-1.5 h-1.5 rounded-full bg-[#00A796]" />
                        พร้อมเพย์ ทุกธนาคาร
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 leading-tight">
                      สแกนจ่ายผ่านระบบ Thai QR พร้อมเพย์ ทุกธนาคาร ตรวจสอบยอดทันที
                    </div>
                  </div>
                  <div className={`w-4.5 h-4.5 rounded-full border flex items-center justify-center shrink-0 mt-0.5 ${
                    (paymentMethod as string) === 'PROMPTPAY'
                      ? 'border-[#2A59FE] bg-[#2A59FE] text-white'
                      : 'border-slate-300 bg-white'
                  }`}>
                    {(paymentMethod as string) === 'PROMPTPAY' && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </div>
                </div>
              )}

              {/* 3. Card */}
              {paymentSettings.card && (
                <div
                  id="checkout-modal-pay-card"
                  onClick={() => setPaymentMethod('CREDIT_CARD' as any)}
                  className={`p-3.5 rounded-2xl border-2 transition-all cursor-pointer flex items-start gap-3 select-none ${
                    (paymentMethod as string) === 'CREDIT_CARD'
                      ? 'border-[#2A59FE] bg-blue-50/20 shadow-2xs'
                      : 'border-slate-200 hover:border-blue-200 bg-white'
                  }`}
                >
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                    (paymentMethod as string) === 'CREDIT_CARD'
                      ? 'bg-[#2A59FE] text-white shadow-sm shadow-blue-500/20'
                      : 'bg-slate-100 text-slate-500'
                  }`}>
                    <CreditCard className="w-4.5 h-4.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1">
                      <div className="text-xs font-bold text-slate-900">
                        บัตรเครดิต/บัตรเดบิต
                      </div>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        (paymentMethod as string) === 'CREDIT_CARD'
                          ? 'text-[#2A59FE] bg-blue-100/80 border border-blue-200'
                          : 'text-slate-500 bg-slate-100'
                      }`}>
                        Secure Card
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 leading-tight">
                      รองรับบัตรเครดิตและเดบิตชั้นนำทุกธนาคาร (Visa, Mastercard, JCB)
                    </div>
                  </div>
                  <div className={`w-4.5 h-4.5 rounded-full border flex items-center justify-center shrink-0 mt-0.5 ${
                    (paymentMethod as string) === 'CREDIT_CARD'
                      ? 'border-[#2A59FE] bg-[#2A59FE] text-white'
                      : 'border-slate-300 bg-white'
                  }`}>
                    {(paymentMethod as string) === 'CREDIT_CARD' && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </div>
                </div>
              )}

              {!paymentSettings.cod && !paymentSettings.qr && !paymentSettings.card && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-xs">
                  ยังไม่ได้เปิดใช้งานช่องทางชำระเงิน กรุณาติดต่อแอดมิน
                </div>
              )}
            </div>
          </div>

          {/* Section 3: Order Summary & Checkout Button */}
          <div className="pt-2 border-t border-slate-100 space-y-3">
            <div className="p-4 bg-slate-50/80 rounded-2xl border border-slate-100 space-y-2 text-xs text-slate-600">
              <div className="flex justify-between">
                <span>ยอดรวมสินค้า ({cart.reduce((a, b) => a + b.quantity, 0)} ชิ้น):</span>
                <span className="font-semibold text-slate-800">฿{subtotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>ค่าจัดส่ง:</span>
                <span className={shippingFee === 0 ? 'text-emerald-600 font-bold' : 'text-slate-800'}>
                  {shippingFee === 0 ? 'จัดส่งฟรี (ครบ 500.-)' : `฿${shippingFee}`}
                </span>
              </div>
              <div className="flex justify-between text-slate-700">
                <span>ค่าบริการเก็บเงินปลายทาง (COD):</span>
                <span className="font-bold text-emerald-600">ฟรี</span>
              </div>

              <div className="pt-2.5 border-t border-slate-200 flex justify-between items-baseline text-slate-900">
                <span className="text-sm font-extrabold">ยอดชำระสุทธิ (เก็บปลายทาง):</span>
                <span className="text-2xl font-black text-[#2A59FE] font-mono">
                  ฿{totalAmount.toLocaleString()}
                </span>
              </div>
            </div>

            <button
              type="submit"
              disabled={submittingOrder}
              id="confirm-order-button"
              className="w-full py-3.5 px-4 bg-gradient-to-r from-[#2A59FE] via-[#3B66FE] to-[#4F46E5] hover:opacity-95 text-white rounded-2xl text-sm font-bold flex items-center justify-center gap-2 shadow-lg shadow-blue-500/25 transition-all active:scale-[0.98] disabled:opacity-60 cursor-pointer"
            >
              {submittingOrder ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  กำลังบันทึกคำสั่งซื้อ & ออกใบเสร็จ...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  ยืนยันสั่งซื้อสินค้า (เก็บเงินปลายทาง)
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Transaction 6-digit PIN Security Gate */}
      <PinVerificationModal
        isOpen={isPinModalOpen}
        onClose={() => setIsPinModalOpen(false)}
        user={currentUser}
        expectedPin={currentUser?.pinHash}
        title={pinCheckData?.title}
        description={pinCheckData?.description}
        securityReason={pinCheckData?.reason}
        orderAmount={totalAmount}
        onSuccess={() => {
          setIsPinModalOpen(false);
          executeOrderSubmission();
        }}
      />
    </div>
  );
};
